import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Article,
  ArticlePayload,
  ArticleReferences,
  createArticle,
  deleteArticle,
  getArticleReferences,
  getArticles,
  updateArticle,
} from '../services/api';
import { SortState, compareValues, toggleSort } from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';
import './ArticlesPage.css';

type ArticleSortKey =
  | 'budget_article'
  | 'work_type_names'
  | 'cost_center'
  | 'category'
  | 'section'
  | 'margin_article'
  | 'planfact_article';

type ArticlesFilterMenuKey =
  | 'work_type'
  | 'cost_center'
  | 'category'
  | 'section'
  | 'margin_article'
  | 'planfact_article'
  | null;

const EMPTY_FORM: ArticlePayload = {
  budget_article: '',
  cost_center: '',
  category: '',
  section: '',
  margin_article: '',
  planfact_article: '',
  work_type_ids: [],
  department_ids: [],
};

function joinNames(values: string[]): string {
  return values.length > 0 ? values.join(', ') : '—';
}

function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  const set = new Set<string>();
  for (const value of values) {
    set.add(displayValue(value));
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const set = new Set<string>();
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) set.add(trimmed);
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
}

type SuggestInputProps = {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  listId: string;
  disabled?: boolean;
};

const SuggestInput: React.FC<SuggestInputProps> = ({
  value,
  onChange,
  options,
  listId,
  disabled,
}) => (
  <>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      list={listId}
      disabled={disabled}
      autoComplete="off"
    />
    <datalist id={listId}>
      {options.map((option) => (
        <option key={option} value={option} />
      ))}
    </datalist>
  </>
);

function matchesSearch(article: Article, query: string): boolean {
  if (!query.trim()) return true;
  const haystack = [
    article.budget_article,
    article.cost_center,
    article.category,
    article.section,
    article.margin_article,
    article.planfact_article,
    article.work_type_names.join(' '),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

function matchesMultiFilter(selected: string[], values: string[]): boolean {
  if (selected.length === 0) return true;
  if (values.length === 0) return selected.includes('—');
  return values.some((value) => selected.includes(value));
}

function matchesScalarFilter(selected: string[], value: string | null | undefined): boolean {
  if (selected.length === 0) return true;
  return selected.includes(displayValue(value));
}

const ArticlesPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('MASTERDATA', 'EDIT');
  const canCreate = can('MASTERDATA', 'CREATE');
  const canEdit = can('MASTERDATA', 'EDIT');
  const canDelete = can('MASTERDATA', 'DELETE');

  const [articles, setArticles] = useState<Article[]>([]);
  const [references, setReferences] = useState<ArticleReferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<ArticlePayload>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<ArticlesFilterMenuKey>(null);
  const [budgetArticleFilter, setBudgetArticleFilter] = useState('');
  const [filterWorkTypes, setFilterWorkTypes] = useState<string[]>([]);
  const [filterCostCenters, setFilterCostCenters] = useState<string[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterSections, setFilterSections] = useState<string[]>([]);
  const [filterMarginArticles, setFilterMarginArticles] = useState<string[]>([]);
  const [filterPlanfactArticles, setFilterPlanfactArticles] = useState<string[]>([]);
  const [sortState, setSortState] = useState<SortState<ArticleSortKey>>({
    key: 'budget_article',
    direction: 'asc',
  });

  const load = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setArticles([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [rows, refs] = await Promise.all([getArticles(), getArticleReferences()]);
      setArticles(rows);
      setReferences(refs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const selectedArticle = useMemo(
    () => (selectedId == null ? null : articles.find((row) => row.id === selectedId) ?? null),
    [articles, selectedId],
  );

  useEffect(() => {
    if (selectedId != null && !articles.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [articles, selectedId]);

  const workTypeFilterOptions = useMemo(
    () => {
      const set = new Set<string>([
        ...(references?.work_types ?? []).map((wt) => wt.short_name),
        ...articles.flatMap((row) => row.work_type_names),
      ]);
      if (set.size === 0) set.add('—');
      return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
    },
    [articles, references],
  );

  const costCenterFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.cost_center)),
    [articles],
  );

  const categoryFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.category)),
    [articles],
  );

  const sectionFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.section)),
    [articles],
  );

  const marginArticleFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.margin_article)),
    [articles],
  );

  const planfactArticleFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.planfact_article)),
    [articles],
  );

  const budgetArticleSuggestions = useMemo(
    () => uniqueNonEmpty(articles.map((row) => row.budget_article)),
    [articles],
  );

  const costCenterSuggestions = useMemo(
    () => uniqueNonEmpty(articles.map((row) => row.cost_center)),
    [articles],
  );

  const categorySuggestions = useMemo(
    () => uniqueNonEmpty(articles.map((row) => row.category)),
    [articles],
  );

  const sectionSuggestions = useMemo(
    () => uniqueNonEmpty(articles.map((row) => row.section)),
    [articles],
  );

  const clearFilters = (): void => {
    setBudgetArticleFilter('');
    setFilterWorkTypes([]);
    setFilterCostCenters([]);
    setFilterCategories([]);
    setFilterSections([]);
    setFilterMarginArticles([]);
    setFilterPlanfactArticles([]);
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    budgetArticleFilter.trim() !== '' ||
    filterWorkTypes.length > 0 ||
    filterCostCenters.length > 0 ||
    filterCategories.length > 0 ||
    filterSections.length > 0 ||
    filterMarginArticles.length > 0 ||
    filterPlanfactArticles.length > 0;

  const filteredArticles = useMemo(() => {
    const budgetQuery = budgetArticleFilter.trim().toLowerCase();
    return articles.filter((row) => {
      const matchesBudget = !budgetQuery || row.budget_article.toLowerCase().includes(budgetQuery);
      return (
        matchesSearch(row, searchText) &&
        matchesBudget &&
        matchesMultiFilter(filterWorkTypes, row.work_type_names) &&
        matchesScalarFilter(filterCostCenters, row.cost_center) &&
        matchesScalarFilter(filterCategories, row.category) &&
        matchesScalarFilter(filterSections, row.section) &&
        matchesScalarFilter(filterMarginArticles, row.margin_article) &&
        matchesScalarFilter(filterPlanfactArticles, row.planfact_article)
      );
    });
  }, [
    articles,
    searchText,
    budgetArticleFilter,
    filterWorkTypes,
    filterCostCenters,
    filterCategories,
    filterSections,
    filterMarginArticles,
    filterPlanfactArticles,
  ]);

  const sortedArticles = useMemo(() => {
    const sorted = [...filteredArticles];
    sorted.sort((a, b) => {
      const key = sortState.key;
      const left = key === 'work_type_names'
        ? joinNames(a[key])
        : (a[key] ?? '');
      const right = key === 'work_type_names'
        ? joinNames(b[key])
        : (b[key] ?? '');
      return compareValues(left, right, sortState.direction);
    });
    return sorted;
  }, [filteredArticles, sortState]);

  useEffect(() => {
    if (selectedId != null && !sortedArticles.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [sortedArticles, selectedId]);

  const openCreateModal = (): void => {
    setFormData(EMPTY_FORM);
    setFormError('');
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = (): void => {
    if (!selectedArticle) return;
    setFormData({
      budget_article: selectedArticle.budget_article,
      cost_center: selectedArticle.cost_center ?? '',
      category: selectedArticle.category ?? '',
      section: selectedArticle.section ?? '',
      margin_article: selectedArticle.margin_article ?? '',
      planfact_article: selectedArticle.planfact_article ?? '',
      work_type_ids: [...selectedArticle.work_type_ids],
      department_ids: [...selectedArticle.department_ids],
    });
    setFormError('');
    setModalMode('edit');
    setModalOpen(true);
  };

  const closeModal = (): void => {
    if (saving) return;
    setModalOpen(false);
    setFormError('');
  };

  const toggleWorkType = (id: number): void => {
    setFormData((prev) => ({
      ...prev,
      work_type_ids: prev.work_type_ids.includes(id)
        ? prev.work_type_ids.filter((value) => value !== id)
        : [...prev.work_type_ids, id],
    }));
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!formData.budget_article.trim()) {
      setFormError('Укажите статью бюджета');
      return;
    }
    setSaving(true);
    setFormError('');
    const payload: ArticlePayload = {
      ...formData,
      budget_article: formData.budget_article.trim(),
      cost_center: formData.cost_center?.trim() || null,
      category: formData.category?.trim() || null,
      section: formData.section?.trim() || null,
      margin_article: formData.margin_article?.trim() || null,
      planfact_article: formData.planfact_article?.trim() || null,
    };
    try {
      if (modalMode === 'create') {
        const created = await createArticle(payload);
        setArticles((prev) => [...prev, created]);
        setSelectedId(created.id);
      } else if (selectedArticle) {
        const updated = await updateArticle(selectedArticle.id, payload);
        setArticles((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      }
      setModalOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedArticle || isDeleting) return;
    const confirmed = window.confirm(`Удалить статью «${selectedArticle.budget_article}»?`);
    if (!confirmed) return;
    setIsDeleting(true);
    setError('');
    try {
      await deleteArticle(selectedArticle.id);
      setArticles((prev) => prev.filter((row) => row.id !== selectedArticle.id));
      setSelectedId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка удаления');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSort = (key: ArticleSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  return (
    <div className="data-page articles-page">
      {!canView ? (
        <div className="empty-state">Нет доступа к мастерданным (требуется право MASTERDATA:EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Статьи</h1>
            <div className="page-header-row__actions">
              {canCreate ? (
                <button type="button" className="create-button" onClick={openCreateModal}>
                  Создать
                </button>
              ) : null}
              {canEdit && selectedArticle ? (
                <button type="button" className="secondary-button" onClick={openEditModal}>
                  Редактировать
                </button>
              ) : null}
              {canDelete && selectedArticle ? (
                <button
                  type="button"
                  className="delete-row-button"
                  onClick={() => void handleDelete()}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Удаление…' : 'Удалить'}
                </button>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <label>
                    Статья бюджета
                    <input
                      value={budgetArticleFilter}
                      onChange={(e) => setBudgetArticleFilter(e.target.value)}
                    />
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Тип задачи"
                      options={workTypeFilterOptions}
                      selected={filterWorkTypes}
                      onSelectedChange={setFilterWorkTypes}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'work_type'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'work_type' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="МВЗ"
                      options={costCenterFilterOptions}
                      selected={filterCostCenters}
                      onSelectedChange={setFilterCostCenters}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'cost_center'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'cost_center' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Категория"
                      options={categoryFilterOptions}
                      selected={filterCategories}
                      onSelectedChange={setFilterCategories}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'category'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'category' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Раздел"
                      options={sectionFilterOptions}
                      selected={filterSections}
                      onSelectedChange={setFilterSections}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'section'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'section' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья из расчёта маржи"
                      options={marginArticleFilterOptions}
                      selected={filterMarginArticles}
                      onSelectedChange={setFilterMarginArticles}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'margin_article'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'margin_article' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья из Планфакта"
                      options={planfactArticleFilterOptions}
                      selected={filterPlanfactArticles}
                      onSelectedChange={setFilterPlanfactArticles}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'planfact_article'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'planfact_article' : null)}
                    />
                  </div>
                </div>
              </aside>
            )}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table articles-page__table">
                      <colgroup>
                        <col className="articles-page__colgrid articles-page__colgrid--budget" />
                        <col className="articles-page__colgrid articles-page__colgrid--work-type" />
                        <col className="articles-page__colgrid articles-page__colgrid--short" />
                        <col className="articles-page__colgrid articles-page__colgrid--text" />
                        <col className="articles-page__colgrid articles-page__colgrid--text" />
                        <col className="articles-page__colgrid articles-page__colgrid--text" />
                        <col className="articles-page__colgrid articles-page__colgrid--planfact" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className="sortable articles-page__th-budget"
                            onClick={() => handleSort('budget_article')}
                            role="button"
                          >
                            Статья бюджета
                          </th>
                          <th
                            className="sortable articles-page__th-work-type"
                            onClick={() => handleSort('work_type_names')}
                            role="button"
                          >
                            Тип задачи
                          </th>
                          <th
                            className="sortable articles-page__th-short"
                            onClick={() => handleSort('cost_center')}
                            role="button"
                          >
                            МВЗ
                          </th>
                          <th className="sortable" onClick={() => handleSort('category')} role="button">
                            Категория
                          </th>
                          <th className="sortable" onClick={() => handleSort('section')} role="button">
                            Раздел
                          </th>
                          <th className="sortable" onClick={() => handleSort('margin_article')} role="button">
                            Статья из расчёта маржи
                          </th>
                          <th className="sortable articles-page__th-planfact" onClick={() => handleSort('planfact_article')} role="button">
                            Статья из Планфакта
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedArticles.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedArticles.map((row) => (
                            <tr
                              key={row.id}
                              className={`clickable-row${selectedId === row.id ? ' data-table__row--selected' : ''}`}
                              onClick={() => setSelectedId(row.id)}
                            >
                              <td className="articles-page__td-budget">{row.budget_article}</td>
                              <td className="articles-page__td-work-type">
                                <div className="articles-page__clamp-2">{joinNames(row.work_type_names)}</div>
                              </td>
                              <td className="articles-page__td-short">{displayValue(row.cost_center)}</td>
                              <td>{displayValue(row.category)}</td>
                              <td>{displayValue(row.section)}</td>
                              <td>{displayValue(row.margin_article)}</td>
                              <td className="articles-page__td-planfact">{displayValue(row.planfact_article)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer articles-page__table">
                      <colgroup>
                        <col className="articles-page__colgrid articles-page__colgrid--budget" />
                        <col className="articles-page__colgrid articles-page__colgrid--work-type" />
                        <col className="articles-page__colgrid articles-page__colgrid--short" />
                        <col className="articles-page__colgrid articles-page__colgrid--text" />
                        <col className="articles-page__colgrid articles-page__colgrid--text" />
                        <col className="articles-page__colgrid articles-page__colgrid--text" />
                        <col className="articles-page__colgrid articles-page__colgrid--planfact" />
                      </colgroup>
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedArticles.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>

          {modalOpen ? (
            <div className="articles-page__modal-overlay" role="presentation" onClick={closeModal}>
              <div
                className="articles-page__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="articles-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="articles-modal-title">
                  {modalMode === 'create' ? 'Новая статья' : 'Редактирование статьи'}
                </h2>
                <form className="articles-page__form" onSubmit={(e) => void handleSubmit(e)}>
                  <label className="articles-page__field">
                    <span>Статья бюджета *</span>
                    <SuggestInput
                      value={formData.budget_article}
                      onChange={(budget_article) => setFormData((prev) => ({ ...prev, budget_article }))}
                      options={budgetArticleSuggestions}
                      listId="articles-suggest-budget-article"
                      disabled={saving}
                    />
                  </label>
                  <label className="articles-page__field">
                    <span>МВЗ</span>
                    <SuggestInput
                      value={formData.cost_center ?? ''}
                      onChange={(cost_center) => setFormData((prev) => ({ ...prev, cost_center }))}
                      options={costCenterSuggestions}
                      listId="articles-suggest-cost-center"
                      disabled={saving}
                    />
                  </label>
                  <label className="articles-page__field">
                    <span>Категория</span>
                    <SuggestInput
                      value={formData.category ?? ''}
                      onChange={(category) => setFormData((prev) => ({ ...prev, category }))}
                      options={categorySuggestions}
                      listId="articles-suggest-category"
                      disabled={saving}
                    />
                  </label>
                  <label className="articles-page__field">
                    <span>Раздел</span>
                    <SuggestInput
                      value={formData.section ?? ''}
                      onChange={(section) => setFormData((prev) => ({ ...prev, section }))}
                      options={sectionSuggestions}
                      listId="articles-suggest-section"
                      disabled={saving}
                    />
                  </label>
                  <label className="articles-page__field">
                    <span>Статья из расчёта маржи</span>
                    <input
                      type="text"
                      value={formData.margin_article ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, margin_article: e.target.value }))}
                      disabled={saving}
                    />
                  </label>
                  <label className="articles-page__field">
                    <span>Статья из Планфакта</span>
                    <input
                      type="text"
                      value={formData.planfact_article ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, planfact_article: e.target.value }))}
                      disabled={saving}
                    />
                  </label>

                  <div className="articles-page__field">
                    <span>Тип задачи</span>
                    <div className="articles-page__checkbox-list">
                      {(references?.work_types ?? []).map((workType) => (
                        <label key={workType.id} className="articles-page__checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.work_type_ids.includes(workType.id)}
                            onChange={() => toggleWorkType(workType.id)}
                            disabled={saving}
                          />
                          <span>{workType.short_name} — {workType.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {formError ? <div className="error-message">{formError}</div> : null}

                  <div className="articles-page__modal-actions">
                    <button type="button" className="secondary-button" onClick={closeModal} disabled={saving}>
                      Отмена
                    </button>
                    <button type="submit" className="create-button" disabled={saving}>
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ArticlesPage;
